import { useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsArrowClockwise, BsCheckCircle, BsStars } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import { useErrorStore } from '#lib/stores/errorStore';

import { useDealsList } from '../../../models/deals/queries';
import { useRegenerateDailyPlan } from '../../../models/insights/mutations';
import { useDailyPlan } from '../../../models/insights/queries';
import AiSuggestionCard from '../../dashboard/AiSuggestionCard/AiSuggestionCard';

const AiTodayTodoList = ({ onOpenCompany }) => {
	const { data, isLoading } = useDailyPlan();
	const regenerate = useRegenerateDailyPlan();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const items = useMemo(() => data?.items ?? [], [data?.items]);
	const dealIds = useMemo(() => [...new Set(items.map((s) => s.dealId).filter(Boolean))], [items]);

	// Load deals to resolve dealId → crmCompanyId
	const { data: dealsData } = useDealsList({ status: 'open', pageSize: 500 }, { enabled: dealIds.length > 0 });
	const dealToCompany = useMemo(() => {
		const map = new Map();
		for (const d of dealsData?.deals ?? []) map.set(d.id, d.crmCompanyId);
		return map;
	}, [dealsData]);

	const handleRegenerate = async () => {
		try {
			await regenerate.mutateAsync();
			showSuccess({
				title: 'Tagesplan wird neu generiert',
				message: 'Das kann 3–5 Minuten dauern. Der neue Plan erscheint automatisch.',
			});
		} catch (err) {
			showError({ error: err, title: 'Tagesplan konnte nicht neu generiert werden' });
		}
	};

	const handleClick = (s) => {
		const companyId = s.crmCompanyId ?? dealToCompany.get(s.dealId);
		if (companyId) onOpenCompany?.(companyId);
	};

	return (
		<SectionCard
			title="KI-Vorschläge für heute"
			icon={BsStars}
			headerAction={
				<Button variant="outline" size="sm" onClick={handleRegenerate} loading={regenerate.isPending}>
					<BsArrowClockwise className="w-3 h-3 mr-1" />
					Neu generieren
				</Button>
			}
		>
			{isLoading && <p className="text-bgBase-800 text-sm">Tagesplan wird geladen …</p>}

			{!isLoading && items.length === 0 && (
				<div className="py-4 text-center">
					<BsCheckCircle className="w-8 h-8 text-success-500 mx-auto mb-2" />
					<p className="text-sm text-text font-medium">Keine offenen Aufgaben</p>
					<p className="text-xs text-bgBase-800 mt-1 max-w-md mx-auto">
						Entweder haben Sie heute schon alles abgearbeitet oder es liegen noch keine offenen Deals vor.
						Klicken Sie auf &bdquo;Neu generieren&ldquo;, um einen neuen Plan zu erstellen.
					</p>
				</div>
			)}

			<div className="space-y-2">
				{items.map((s) => (
					<div key={s.id} onClick={() => handleClick(s)} className="cursor-pointer">
						<AiSuggestionCard suggestion={s} />
					</div>
				))}
			</div>
		</SectionCard>
	);
};

AiTodayTodoList.propTypes = {
	onOpenCompany: PropTypes.func,
};

export default AiTodayTodoList;
